import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
    Plus, Pencil, Trash, UploadSimple, FileText, Receipt, CheckCircle, X,
    ArrowsClockwise, MagnifyingGlass, Sparkle, Download,
} from "@phosphor-icons/react";

const fmtMoney = (n, ccy = "USD") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(Number(n || 0));

const pctTone = (pct) =>
    pct >= 100 ? "text-accent" : pct >= 80 ? "text-amber-600" : "text-emerald-700";

const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ----- Upload drop zone with AI extraction -----
function UploadZone({ onUploaded, busy, setBusy }) {
    const inputRef = useRef(null);
    const [hover, setHover] = useState(false);

    const upload = async (files) => {
        if (!files?.length) return;
        setBusy(true);
        let ok = 0;
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                const r = await api.post("/budget/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                ok++;
                toast.success(`Extracted "${r.data.vendor || file.name}"`, {
                    description: r.data.ai_confidence
                        ? `Confidence ${(r.data.ai_confidence * 100).toFixed(0)}%`
                        : "Review the suggested fields and confirm.",
                });
            } catch (e) {
                toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
            }
        }
        setBusy(false);
        if (ok) await onUploaded();
    };

    return (
        <div
            data-testid="budget-upload-zone"
            onDragOver={(e) => { e.preventDefault(); setHover(true); }}
            onDragLeave={() => setHover(false)}
            onDrop={(e) => { e.preventDefault(); setHover(false); upload(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`group relative border-2 border-dashed rounded-sm py-10 px-6 cursor-pointer text-center transition-colors ${hover ? "border-primary bg-primary/5" : "border-border hover:border-foreground/50"} ${busy ? "opacity-60 pointer-events-none" : ""}`}
        >
            <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => upload(Array.from(e.target.files))}
                data-testid="budget-upload-input"
            />
            <div className="flex items-center justify-center gap-3">
                {busy
                    ? <ArrowsClockwise size={22} weight="duotone" className="text-primary animate-spin" />
                    : <UploadSimple size={22} weight="duotone" className="text-primary" />}
                <div className="text-left">
                    <div className="font-display font-semibold text-[14px]">
                        {busy ? "Reading your receipts…" : "Drop receipts or invoices here"}
                    </div>
                    <div className="editorial text-[12px] text-muted-foreground">
                        PDF, JPG, PNG, HEIC. Multiple files welcome. AI extracts vendor, amount, date, and suggests a category.
                    </div>
                </div>
            </div>
        </div>
    );
}

// ----- Pending review card (after AI extraction) -----
function PendingCard({ expense, categories, lines, onConfirm, onReject }) {
    const [vendor, setVendor] = useState(expense.vendor || "");
    const [amount, setAmount] = useState(expense.amount || 0);
    const [currency, setCurrency] = useState(expense.currency || "USD");
    const [date, setDate] = useState(expense.expense_date || "");
    const [categoryId, setCategoryId] = useState(expense.category_id || "");
    const [lineId, setLineId] = useState(expense.line_id || "");
    const [description, setDescription] = useState(expense.description || "");
    const [saving, setSaving] = useState(false);

    const availableLines = useMemo(
        () => lines.filter((ln) => !categoryId || ln.category_id === categoryId),
        [lines, categoryId],
    );

    const confirm = async () => {
        setSaving(true);
        try {
            await api.patch(`/budget/expenses/${expense.id}`, {
                vendor, amount: parseFloat(amount), currency,
                expense_date: date || null,
                category_id: categoryId || null,
                line_id: lineId || null,
                description,
                status: "confirmed",
            });
            toast.success("Confirmed");
            await onConfirm();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const reject = async () => {
        if (!window.confirm("Reject and delete this extracted expense?")) return;
        await api.delete(`/budget/expenses/${expense.id}`);
        await onReject();
    };

    const confidence = Math.round((expense.ai_confidence || 0) * 100);
    const confTone = confidence >= 70 ? "text-emerald-700" : confidence >= 40 ? "text-amber-600" : "text-accent";

    return (
        <article
            data-testid={`pending-${expense.id}`}
            className="border border-primary/40 bg-primary/5 rounded-sm p-5 space-y-4"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkle size={14} weight="duotone" className="text-primary" />
                    <span className="overline text-[9px] text-primary">AI Review</span>
                    <span className={`font-mono text-[10px] tracking-wider ${confTone}`}>{confidence}% confidence</span>
                </div>
                {expense.has_file && (
                    <a
                        href={`${api.defaults.baseURL}/budget/expenses/${expense.id}/file`}
                        target="_blank" rel="noreferrer"
                        className="font-mono text-[10px] tracking-wider uppercase text-primary border-b border-primary/40 hover:border-primary inline-flex items-center gap-1"
                        title="Open the original uploaded file in a new tab"
                    >
                        <FileText size={11} /> {expense.file_name}
                    </a>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label className="overline text-[9px]">Vendor</Label>
                    <Input value={vendor} onChange={(e) => setVendor(e.target.value)} className="rounded-sm h-8 text-sm" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                        <Label className="overline text-[9px]">Amount</Label>
                        <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-sm h-8 text-sm tabular-nums" />
                    </div>
                    <div>
                        <Label className="overline text-[9px]">Ccy</Label>
                        <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className="rounded-sm h-8 text-sm font-mono" maxLength={4} />
                    </div>
                </div>
                <div>
                    <Label className="overline text-[9px]">Date</Label>
                    <Input type="date" value={date || ""} onChange={(e) => setDate(e.target.value)} className="rounded-sm h-8 text-sm" />
                </div>
                <div>
                    <Label className="overline text-[9px]">Category</Label>
                    <Select value={categoryId || ""} onValueChange={(v) => { setCategoryId(v); setLineId(""); }}>
                        <SelectTrigger className="rounded-sm h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-2">
                    <Label className="overline text-[9px]">Channel / tactic</Label>
                    <Select value={lineId || ""} onValueChange={setLineId} disabled={!availableLines.length}>
                        <SelectTrigger className="rounded-sm h-8 text-sm">
                            <SelectValue placeholder={availableLines.length ? "Pick a channel" : "Add a channel to this category first"} />
                        </SelectTrigger>
                        <SelectContent>
                            {availableLines.map((ln) => <SelectItem key={ln.id} value={ln.id}>{ln.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-2">
                    <Label className="overline text-[9px]">Description</Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-sm text-sm" />
                </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-border">
                <Button data-testid={`pending-reject-${expense.id}`} variant="ghost" size="sm" onClick={reject} className="rounded-sm text-accent">
                    <X size={12} className="mr-1" /> Reject
                </Button>
                <Button data-testid={`pending-confirm-${expense.id}`} size="sm" onClick={confirm} disabled={saving} className="rounded-sm">
                    {saving ? <ArrowsClockwise size={12} className="mr-1 animate-spin" /> : <CheckCircle size={12} className="mr-1" />}
                    Confirm & add to budget
                </Button>
            </div>
        </article>
    );
}

// ----- Category / Line editor dialog -----
function LineDialog({ open, onOpenChange, categories, editing, onSaved }) {
    const [categoryId, setCategoryId] = useState(editing?.category_id || categories[0]?.id || "");
    const [name, setName] = useState(editing?.name || "");
    const [budget, setBudget] = useState(editing?.budget_amount || 0);
    const [notes, setNotes] = useState(editing?.notes || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setCategoryId(editing?.category_id || categories[0]?.id || "");
        setName(editing?.name || "");
        setBudget(editing?.budget_amount || 0);
        setNotes(editing?.notes || "");
    }, [editing, categories]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { category_id: categoryId, name, budget_amount: parseFloat(budget) || 0, notes };
            if (editing?.id) {
                await api.patch(`/budget/lines/${editing.id}`, payload);
            } else {
                await api.post("/budget/lines", payload);
            }
            await onSaved();
            onOpenChange(false);
            toast.success(editing?.id ? "Channel updated" : "Channel added");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-md" data-testid="line-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{editing?.id ? "Edit channel" : "Add channel / tactic"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="overline text-[9px]">Category</Label>
                        <Select value={categoryId} onValueChange={setCategoryId}>
                            <SelectTrigger className="rounded-sm" data-testid="line-cat-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="overline text-[9px]">Channel / tactic name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="line-name-input"
                            placeholder="e.g. Influencer seeding (mid-tier)" />
                    </div>
                    <div>
                        <Label className="overline text-[9px]">Planned budget (USD)</Label>
                        <Input type="number" step="100" value={budget} onChange={(e) => setBudget(e.target.value)}
                            className="rounded-sm tabular-nums" data-testid="line-budget-input" />
                    </div>
                    <div>
                        <Label className="overline text-[9px]">Notes</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={save} disabled={saving || !name || !categoryId} data-testid="line-save-btn" className="rounded-sm">
                        {saving ? <ArrowsClockwise size={12} className="mr-1 animate-spin" /> : null}
                        {editing?.id ? "Save" : "Add channel"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CategoryDialog({ open, onOpenChange, editing, onSaved }) {
    const [name, setName] = useState(editing?.name || "");
    const [description, setDescription] = useState(editing?.description || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setName(editing?.name || "");
        setDescription(editing?.description || "");
    }, [editing]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { name, description };
            if (editing?.id) {
                await api.patch(`/budget/categories/${editing.id}`, payload);
            } else {
                await api.post("/budget/categories", payload);
            }
            await onSaved();
            onOpenChange(false);
            toast.success(editing?.id ? "Category updated" : "Category added");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-md" data-testid="cat-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{editing?.id ? "Edit category" : "Add category"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="overline text-[9px]">Category name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="cat-name-input" />
                    </div>
                    <div>
                        <Label className="overline text-[9px]">Description (optional)</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-sm" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={save} disabled={saving || !name} data-testid="cat-save-btn" className="rounded-sm">
                        {saving ? <ArrowsClockwise size={12} className="mr-1 animate-spin" /> : null}
                        {editing?.id ? "Save" : "Add category"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ----- Main page -----
const WINDOW_LABELS = { monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };

export default function Budget() {
    const [summary, setSummary] = useState(null);
    const [categories, setCategories] = useState([]);
    const [lines, setLines] = useState([]);
    const [pending, setPending] = useState([]);
    const [confirmed, setConfirmed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [lineDialog, setLineDialog] = useState({ open: false, editing: null });
    const [catDialog, setCatDialog] = useState({ open: false, editing: null });
    const [filter, setFilter] = useState("");
    const [window_, setWindow_] = useState(() => localStorage.getItem("haevn-budget-window") || "monthly");

    const load = async () => {
        const [sumR, lnR, pendR, confR] = await Promise.all([
            api.get(`/budget/summary?window=${window_}`),
            api.get("/budget/lines"),
            api.get("/budget/expenses?status=pending_review"),
            api.get("/budget/expenses?status=confirmed&limit=200"),
        ]);
        setSummary(sumR.data);
        setCategories(sumR.data.categories);
        setLines(lnR.data);
        setPending(pendR.data);
        setConfirmed(confR.data);
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [window_]);

    useEffect(() => {
        localStorage.setItem("haevn-budget-window", window_);
    }, [window_]);

    const linesByCategory = useMemo(() => {
        const map = {};
        for (const ln of (summary?.lines || [])) {
            (map[ln.category_id] = map[ln.category_id] || []).push(ln);
        }
        return map;
    }, [summary]);

    const exportCSV = () => {
        const headers = ["date", "vendor", "amount", "currency", "category", "channel", "description"];
        const catName = (id) => categories.find((c) => c.id === id)?.name || "";
        const lnName = (id) => lines.find((l) => l.id === id)?.name || "";
        const rows = confirmed.map((e) => [
            e.expense_date || "", e.vendor || "", e.amount, e.currency,
            catName(e.category_id), lnName(e.line_id), (e.description || "").replace(/\n/g, " "),
        ]);
        const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `haevn-budget-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const deleteLine = async (id) => {
        if (!window.confirm("Delete this channel? Any confirmed expenses on it will block deletion.")) return;
        try {
            await api.delete(`/budget/lines/${id}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const deleteCategory = async (id) => {
        if (!window.confirm("Delete this category? It must be empty.")) return;
        try {
            await api.delete(`/budget/categories/${id}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const visibleConfirmed = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return confirmed;
        return confirmed.filter((e) =>
            (e.vendor || "").toLowerCase().includes(q)
            || (e.description || "").toLowerCase().includes(q)
        );
    }, [confirmed, filter]);

    if (loading || !summary) {
        return <div className="px-8 lg:px-14 py-16 text-sm text-muted-foreground">Loading budget…</div>;
    }

    const T = summary.totals;
    return (
        <>
            <header className="px-8 lg:px-14 pt-12 pb-6 border-b border-border">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div className="max-w-[68ch]">
                        <div className="overline mb-3 text-primary">HAEVN Labs · Private</div>
                        <h1 className="font-display font-black tracking-[-0.025em] leading-[0.95] text-[clamp(2rem,3.4vw,2.8rem)]">
                            Marketing budget
                        </h1>
                        <p className="editorial text-muted-foreground mt-3 leading-relaxed">
                            Track spend against planned budget across channels and tactics. Drop a receipt or
                            invoice in and the AI will read vendor, amount, date, and a suggested category — you
                            confirm with a click.
                        </p>
                    </div>
                    <div className="flex items-center gap-6 font-mono text-[11px] tracking-wider uppercase text-muted-foreground">
                        <div data-testid="totals-planned">
                            <div className="overline text-[9px]">Planned · {WINDOW_LABELS[window_]}</div>
                            <div className="font-display font-black text-foreground text-2xl tabular-nums">{fmtMoney(T.planned)}</div>
                        </div>
                        <div data-testid="totals-spent">
                            <div className="overline text-[9px]">Spent</div>
                            <div className="font-display font-black text-foreground text-2xl tabular-nums">{fmtMoney(T.spent)}</div>
                        </div>
                        <div data-testid="totals-remaining">
                            <div className="overline text-[9px]">Remaining</div>
                            <div className={`font-display font-black text-2xl tabular-nums ${T.remaining < 0 ? "text-accent" : "text-emerald-700"}`}>{fmtMoney(T.remaining)}</div>
                        </div>
                        <div data-testid="totals-pending">
                            <div className="overline text-[9px]">Pending</div>
                            <div className="font-display font-black text-foreground text-2xl tabular-nums">{T.pending_review}</div>
                        </div>
                    </div>
                </div>

                <div className="mt-5 flex items-center gap-2" data-testid="window-toggle">
                    <span className="overline text-[9px] mr-1">View</span>
                    {["monthly", "quarterly", "annual"].map((w) => (
                        <button
                            key={w}
                            data-testid={`window-${w}`}
                            onClick={() => setWindow_(w)}
                            className={`px-3 h-7 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${
                                window_ === w
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border bg-card text-muted-foreground hover:text-foreground hover:border-foreground/40"
                            }`}
                        >
                            {WINDOW_LABELS[w]}
                        </button>
                    ))}
                    <span className="ml-2 editorial text-[11px] text-muted-foreground italic">
                        Planned shown for the {WINDOW_LABELS[window_].toLowerCase()} window · spend filtered to this period.
                    </span>
                </div>
            </header>

            <main className="px-8 lg:px-14 py-10 max-w-[1200px] mx-auto space-y-12">
                <UploadZone onUploaded={load} busy={uploading} setBusy={setUploading} />

                {pending.length > 0 && (
                    <section data-testid="pending-section">
                        <div className="overline mb-3">Review queue · {pending.length}</div>
                        <div className="grid lg:grid-cols-2 gap-4">
                            {pending.map((e) => (
                                <PendingCard
                                    key={e.id}
                                    expense={e}
                                    categories={categories}
                                    lines={lines}
                                    onConfirm={load}
                                    onReject={load}
                                />
                            ))}
                        </div>
                    </section>
                )}

                <section data-testid="budget-grid">
                    <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                        <div className="overline">Budget by category</div>
                        <div className="flex gap-1.5">
                            <Button data-testid="add-category-btn" variant="ghost" size="sm" className="rounded-sm h-7 text-[11px]" onClick={() => setCatDialog({ open: true, editing: null })}>
                                <Plus size={12} className="mr-1" /> Category
                            </Button>
                            <Button data-testid="add-line-btn" size="sm" className="rounded-sm h-7 text-[11px]" onClick={() => setLineDialog({ open: true, editing: null })}>
                                <Plus size={12} className="mr-1" /> Channel
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-5">
                        {categories.map((cat) => {
                            const catLines = linesByCategory[cat.id] || [];
                            const planned = cat.planned || 0;
                            const spent = cat.spent || 0;
                            const pct = planned ? (spent / planned) * 100 : 0;
                            return (
                                <article key={cat.id} className="border border-border bg-card rounded-sm" data-testid={`cat-${cat.id}`}>
                                    <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-display font-bold text-[15px] tracking-tight">{cat.name}</h3>
                                                <button data-testid={`edit-cat-${cat.id}`} onClick={() => setCatDialog({ open: true, editing: cat })} className="text-muted-foreground hover:text-foreground" title="Edit category"><Pencil size={11} /></button>
                                                <button data-testid={`del-cat-${cat.id}`} onClick={() => deleteCategory(cat.id)} className="text-muted-foreground hover:text-accent" title="Delete category"><Trash size={11} /></button>
                                            </div>
                                            {cat.description && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{cat.description}</p>}
                                        </div>
                                        <div className="flex items-center gap-6 shrink-0 font-mono text-[11px] tabular-nums">
                                            <div className="text-right">
                                                <div className="overline text-[9px]">Spent / Planned</div>
                                                <div className="text-foreground">{fmtMoney(spent, cat.currency)} <span className="text-muted-foreground">/ {fmtMoney(planned, cat.currency)}</span></div>
                                            </div>
                                            <div className="w-24">
                                                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                                    <div className={`h-full ${pct >= 100 ? "bg-accent" : pct >= 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                                                </div>
                                                <div className={`text-right text-[10px] mt-0.5 ${pctTone(pct)}`}>{pct.toFixed(0)}%</div>
                                            </div>
                                        </div>
                                    </header>
                                    {catLines.length === 0 ? (
                                        <div className="px-4 py-3 text-[12px] italic text-muted-foreground">
                                            No channels yet. Click <span className="not-italic">+ Channel</span> to add one.
                                        </div>
                                    ) : (
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-border bg-muted/30">
                                                    <th className="text-left overline text-[9px] px-4 py-2">Channel / tactic</th>
                                                    <th className="text-right overline text-[9px] px-2 py-2 w-28">Spent</th>
                                                    <th className="text-right overline text-[9px] px-2 py-2 w-28">Planned</th>
                                                    <th className="text-right overline text-[9px] px-2 py-2 w-28">Remaining</th>
                                                    <th className="overline text-[9px] px-2 py-2 w-24">Pace</th>
                                                    <th className="px-2 py-2 w-16"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {catLines.map((ln) => (
                                                    <tr key={ln.id} className="border-b border-border last:border-b-0" data-testid={`line-${ln.id}`}>
                                                        <td className="px-4 py-2.5">
                                                            <div className="font-display text-[13px]">{ln.name}</div>
                                                            {ln.notes && <div className="text-[11px] text-muted-foreground italic line-clamp-1">{ln.notes}</div>}
                                                        </td>
                                                        <td className="text-right tabular-nums font-mono text-[12px] px-2">{fmtMoney(ln.spent, ln.currency)}</td>
                                                        <td className="text-right tabular-nums font-mono text-[12px] text-muted-foreground px-2">{fmtMoney(ln.budget_amount, ln.currency)}</td>
                                                        <td className={`text-right tabular-nums font-mono text-[12px] px-2 ${ln.remaining < 0 ? "text-accent" : ""}`}>{fmtMoney(ln.remaining, ln.currency)}</td>
                                                        <td className="px-2">
                                                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                                                <div className={`h-full ${ln.pct_used >= 100 ? "bg-accent" : ln.pct_used >= 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.min(100, ln.pct_used || 0)}%` }} />
                                                            </div>
                                                            <div className={`text-right text-[10px] mt-0.5 ${pctTone(ln.pct_used)}`}>{(ln.pct_used || 0).toFixed(0)}%</div>
                                                        </td>
                                                        <td className="text-right px-2">
                                                            <button data-testid={`edit-line-${ln.id}`} onClick={() => setLineDialog({ open: true, editing: ln })} className="text-muted-foreground hover:text-foreground mr-2" title="Edit"><Pencil size={11} /></button>
                                                            <button data-testid={`del-line-${ln.id}`} onClick={() => deleteLine(ln.id)} className="text-muted-foreground hover:text-accent" title="Delete"><Trash size={11} /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </section>

                <section data-testid="expense-list">
                    <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                        <div className="overline">Expenses · {confirmed.length}</div>
                        <div className="flex gap-2 items-center">
                            <div className="relative">
                                <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Search vendor or description…"
                                    className="rounded-sm h-8 pl-7 text-xs w-64"
                                    data-testid="expense-filter"
                                />
                            </div>
                            <Button data-testid="export-csv-btn" variant="outline" size="sm" className="rounded-sm h-8 text-[11px]" onClick={exportCSV} disabled={!confirmed.length}>
                                <Download size={12} className="mr-1" /> CSV
                            </Button>
                        </div>
                    </div>

                    {visibleConfirmed.length === 0 ? (
                        <div className="border border-dashed border-border rounded-sm py-10 text-center text-[13px] italic text-muted-foreground">
                            <Receipt size={22} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
                            {confirmed.length === 0
                                ? "No expenses yet. Drop a receipt above to get started."
                                : "No expenses match your search."}
                        </div>
                    ) : (
                        <div className="border border-border bg-card rounded-sm overflow-hidden">
                            <table className="w-full text-[12px]">
                                <thead className="border-b border-border bg-muted/30">
                                    <tr>
                                        <th className="text-left overline text-[9px] px-3 py-2 w-24">Date</th>
                                        <th className="text-left overline text-[9px] px-3 py-2">Vendor</th>
                                        <th className="text-left overline text-[9px] px-3 py-2">Channel</th>
                                        <th className="text-right overline text-[9px] px-3 py-2 w-28">Amount</th>
                                        <th className="text-right overline text-[9px] px-3 py-2 w-20">File</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleConfirmed.map((e) => {
                                        const cat = categories.find((c) => c.id === e.category_id);
                                        const ln = lines.find((l) => l.id === e.line_id);
                                        return (
                                            <tr key={e.id} className="border-b border-border last:border-b-0 hover:bg-muted/20" data-testid={`exp-${e.id}`}>
                                                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground tabular-nums">{fmtDate(e.expense_date)}</td>
                                                <td className="px-3 py-2 font-display">{e.vendor || <span className="text-muted-foreground italic">untitled</span>}</td>
                                                <td className="px-3 py-2">
                                                    <div className="text-foreground/90">{ln?.name || <span className="text-muted-foreground italic">unassigned</span>}</div>
                                                    {cat && <div className="text-[10px] text-muted-foreground">{cat.name}</div>}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtMoney(e.amount, e.currency)}</td>
                                                <td className="px-3 py-2 text-right">
                                                    {e.has_file ? (
                                                        <a
                                                            href={`${api.defaults.baseURL}/budget/expenses/${e.id}/file`}
                                                            target="_blank" rel="noreferrer"
                                                            className="text-primary hover:underline inline-flex items-center gap-1"
                                                            title="Open original receipt"
                                                        >
                                                            <FileText size={11} />
                                                        </a>
                                                    ) : <span className="text-muted-foreground">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </main>

            <LineDialog
                open={lineDialog.open}
                onOpenChange={(o) => setLineDialog({ open: o, editing: o ? lineDialog.editing : null })}
                categories={categories}
                editing={lineDialog.editing}
                onSaved={load}
            />
            <CategoryDialog
                open={catDialog.open}
                onOpenChange={(o) => setCatDialog({ open: o, editing: o ? catDialog.editing : null })}
                editing={catDialog.editing}
                onSaved={load}
            />
        </>
    );
}
